/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { queryTurbineInspections } from "src/services/AmplifyQuery";

import {
  queryInspectionModels,
  queryInspectionResults,
} from "src/services/AmplifyQuery";

import Layout from "src/components/layouts/Layout.react";
import Card from "src/components/cards/Card.react";
import {
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/InspectionTable.react";
import LoadingCard from "src/components/loading/LoadingCard.react";

/**
 * The Inspection Page component
 *
 * @author Thais Watanabe
 */
const Progress = () => {
  const location = useLocation();

  const { inspection } = location.state;

  const [inspectionModels, setInspectionModels] = useState(null);
  const [turbineInspections, setTurbineInspections] = useState([]);
  const [results, setResults] = useState([]);
  const [inpectionItemsSize, setInpectionItemsSize] = useState(0);

  /**
   * useEffect
   */
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  /* useEffect */
  useEffect(() => {
    fetchInspectionModels();
  }, []);

  /**
   * fetchInspectionModels
   */
  async function fetchInspectionModels() {
    queryInspectionModels(null, setInspectionModels);
  }

  /**
   * useEffect
   */
  useEffect(() => {
    fetchTurbineInspections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchTurbineInspections
   */
  async function fetchTurbineInspections() {
    const filter = { inspectionID: { eq: inspection.id } };

    queryTurbineInspections(filter, null, [], setTurbineInspections);
  }

  /* useEffect */
  useEffect(() => {
    fetchInspectionResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchInspectionResults
   */
  async function fetchInspectionResults() {
    const filter = {
      or: turbineInspections.map((obj) => ({
        turbineInspectionID: { eq: obj.id },
      })),
    };

    queryInspectionResults(filter, setResults);
  }

  function findResult(itemId, turbineInspectionId) {
    return results.find(
      (obj) =>
        obj.itemId === itemId && obj.turbineInspectionID === turbineInspectionId
    );
  }

  /* useEffect */
  useEffect(() => {
    if (inspectionModels) {
      setInpectionItemsSize(
        inspectionModels.reduce(
          (partialSum, a) => partialSum + a.inspectionModelItems.items.length,
          0
        )
      );
    }
  }, [inspectionModels]);

  function showProgress(turbineInspectionId) {
    if (results.length > 0 && inpectionItemsSize) {
      const resultSize = results.filter(
        (obj) => obj.turbineInspectionID === turbineInspectionId
      ).length;
      return Math.round((resultSize * 100) / inpectionItemsSize);
    } else {
      return 0;
    }
  }

  return (
    <Layout
      title="進捗一覧表"
      breadcrumbs={["登録済点検一覧", "登録済点検一覧（号機）", "進捗一覧表"]}
    >
      <Card>
        <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
            <div className="ml-4 mt-2">
              <h3 className="text-md leading-6 font-medium text-gray-900">
                {inspection.name}
              </h3>
            </div>
          </div>
        </div>
      </Card>
      
      {!turbineInspections.length && <LoadingCard />}
      {turbineInspections.length && (
        <Card className="table-progress">
          <table className="table-auto w-100">
            <thead>
              <tr>
                <ColHeader className="w-40" rowSpan={2}>
                  項目
                </ColHeader>
                <ColHeader className="w-30" rowSpan={2}>
                  中項目
                </ColHeader>
                {turbineInspections
                  .sort((a, b) =>
                    a["name"].localeCompare(b["name"], undefined, {
                      numeric: true,
                      sensitivity: "base",
                    })
                  )
                  .map((turbineInspection, i) => (
                    <th key={i} className="text-center bg-gray-50 mb-1">
                      <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800">
                        {showProgress(turbineInspection.id)}%
                      </span>
                    </th>
                  ))}
              </tr>
              <tr>
                {turbineInspections
                  .sort((a, b) =>
                    a["name"].localeCompare(b["name"], undefined, {
                      numeric: true,
                      sensitivity: "base",
                    })
                  )
                  .map((turbineInspection, i) => (
                    <ColHeader key={i} className="text-center">
                      {turbineInspection.name}
                    </ColHeader>
                  ))}
              </tr>
            </thead>
            {inspectionModels ? (
              inspectionModels.map((model, i) => (
                <TableBody key={i}>
                  <tr key={i}>
                    <Col
                      className=""
                      rowSpan={model.inspectionModelItems.items.length + 1}
                    >
                      {model.name}
                    </Col>
                  </tr>
                  {model.inspectionModelItems.items.map((item, i) => (
                    <tr key={i}>
                      <Col>{item.description}</Col>
                      {turbineInspections
                        .sort((a, b) =>
                          a["name"].localeCompare(b["name"], undefined, {
                            numeric: true,
                            sensitivity: "base",
                          })
                        )
                        .map((turbineInspection, i) => (
                          <td
                            key={i}
                            className="text-xs text-center text-gray-500"
                          >
                            {findResult(item.name, turbineInspection.id)
                              ?.createdAt &&
                              moment(
                                findResult(item.name, turbineInspection.id)
                                  ?.createdAt
                              ).format("MM-DD")}
                          </td>
                        ))}
                    </tr>
                  ))}
                </TableBody>
              ))
            ) : (
              <TableBody></TableBody>
            )}
          </table>
        </Card>
      )}
    </Layout>
  );
};

export default Progress;
